import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import SonucBelgesi from "@/components/SonucBelgesi";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Pencil, Save, X, BarChart3, FileCheck2, ChevronLeft, CheckCircle2, XCircle, MinusCircle, ChevronDown, Printer } from "lucide-react";

const cleanKey = (v, qc) => (v || "").toUpperCase().replace(/[^ABCD]/g, "").slice(0, qc);
const OPTS = ["A", "B", "C", "D"];
const GRADES = [1, 2, 3, 4, 5, 6, 7];
const normKazanim = (s) => { const qc = s.question_count || 0; const kz = [...(s.kazanims || [])].slice(0, qc); while (kz.length < qc) kz.push(""); return { ...s, kazanims: kz }; };
const gradesOf = (k) => (k.grades && k.grades.length ? k.grades : [k.grade]);

export default function KurumSinavPanel({ grade, isEditor, students, viewGrade = null, viewStudentId = null }) {
  const [list, setList] = useState([]);
  const [editK, setEditK] = useState(null);
  const [activeK, setActiveK] = useState(null);
  const [week, setWeek] = useState(1);
  const [weekSubs, setWeekSubs] = useState([]);
  const [kazanimMap, setKazanimMap] = useState({});
  const [kzOpen, setKzOpen] = useState({});
  const [bulk, setBulk] = useState({});
  const [view, setView] = useState("key"); // key | mark | bulk | eval | series
  const [markStudent, setMarkStudent] = useState("");
  const [markAnswers, setMarkAnswers] = useState({});
  const [markResult, setMarkResult] = useState(null);
  const [evalRows, setEvalRows] = useState([]);
  const [seriesRows, setSeriesRows] = useState([]);
  const [karne, setKarne] = useState(null);
  const [karneSid, setKarneSid] = useState(null);
  const [bulkKarne, setBulkKarne] = useState(null); // { title, items }
  const [bulkLoading, setBulkLoading] = useState(false);
  const [myResult, setMyResult] = useState(null);
  const [gradeFilter, setGradeFilter] = useState(null);
  const [rankRows, setRankRows] = useState(null);
  useEffect(() => { if (isEditor) setGradeFilter(grade); }, [grade, isEditor]);

  const load = () => api.get("/kurum-sinavlar", { params: !isEditor && viewGrade ? { grade: viewGrade } : {} }).then(({ data }) => setList(data));
  useEffect(() => { load(); }, [grade]);

  const subjectsList = async (grs) => {
    const gs = grs?.length ? grs : [grade];
    const res = await Promise.all(gs.map((g) => api.get("/subjects", { params: { grade: g } })));
    const seen = new Set(); const out = [];
    res.flatMap(({ data }) => data).forEach((s) => { if (!seen.has(s.name)) { seen.add(s.name); out.push(s); } });
    return out;
  };

  const openNew = () => setEditK({ name: "", grade, grades: [grade], publisher: "", week_count: 36, subjects: [{ name: "", question_count: 10 }] });
  const openEdit = (k) => { const c = JSON.parse(JSON.stringify(k)); c.grades = c.grades?.length ? c.grades : [c.grade]; setEditK(c); };
  const saveKurum = async () => {
    if (!editK.name.trim()) return toast.error("Kurum sınavı adı gerekli");
    try {
      if (editK.id) await api.put(`/kurum-sinavlar/${editK.id}`, editK);
      else await api.post("/kurum-sinavlar", editK);
      toast.success("Kurum sınavı kaydedildi ✓");
      setEditK(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delKurum = async (id) => { if (!window.confirm("Kurum sınavı, tüm haftaları ve sonuçları silinsin mi?")) return; await api.delete(`/kurum-sinavlar/${id}`); toast.success("Silindi"); if (activeK?.id === id) setActiveK(null); load(); };

  const loadKazanims = async (subs, k) => {
    const m = {};
    await Promise.all(subs.map(async (s) => { const { data } = await api.get("/kurum-kazanims", { params: { grade: k?.grade || grade, subject: s.name } }); m[s.name] = data.kazanims || []; }));
    setKazanimMap(m);
  };
  const loadWeek = async (k, n) => {
    const { data } = await api.get(`/kurum-sinavlar/${k.id}/week/${n}`);
    setWeekSubs(data.subjects.map(normKazanim));
    if (isEditor) {
      loadKazanims(data.subjects, k);
      const { data: res } = await api.get("/kurum-results", { params: { kurum_id: k.id, week_number: n } });
      const b = {}; res.forEach((r) => { b[r.student_id] = r.answers || {}; }); setBulk(b);
    } else {
      const { data: res } = await api.get("/kurum-results", { params: { kurum_id: k.id, week_number: n, ...(viewStudentId ? { student_id: viewStudentId } : {}) } });
      setMyResult(res[0] || null);
    }
  };
  const openKurum = (k) => { setActiveK(k); setWeek(1); setView("key"); loadWeek(k, 1); };
  const changeWeek = (n) => { setWeek(n); setMarkStudent(""); setMarkAnswers({}); setMarkResult(null); setRankRows(null); loadWeek(activeK, n); };

  const setKey = (si, val) => setWeekSubs((ws) => ws.map((s, i) => (i === si ? { ...s, answer_key: cleanKey(val, s.question_count) } : s)));
  const setKz = (si, qi, val) => setWeekSubs((ws) => ws.map((s, i) => { if (i !== si) return s; const kz = [...(s.kazanims || [])]; while (kz.length < s.question_count) kz.push(""); kz[qi] = val; return { ...s, kazanims: kz }; }));
  const saveWeek = async () => {
    try { await api.post(`/kurum-sinavlar/${activeK.id}/week/${week}`, { subjects: weekSubs }); toast.success(`${week}. hafta cevap anahtarı kaydedildi ✓`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setBulkAns = (sid, subj, val, qc) => setBulk((b) => ({ ...b, [sid]: { ...(b[sid] || {}), [subj]: cleanKey(val, qc) } }));
  const saveBulk = async () => {
    const results = Object.entries(bulk).map(([student_id, answers]) => ({ student_id, answers }));
    try { const { data } = await api.post("/kurum-results/bulk", { kurum_id: activeK.id, week_number: week, results }); toast.success(`${data.count} öğrenci değerlendirildi ✓`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const selectMarkStudent = async (sid) => {
    setMarkStudent(sid); setMarkResult(null);
    if (!sid) { setMarkAnswers({}); return; }
    try {
      const { data } = await api.get("/kurum-results", { params: { kurum_id: activeK.id, week_number: week, student_id: sid } });
      setMarkAnswers(data[0]?.answers || {});
      setMarkResult(data[0] || null);
    } catch { setMarkAnswers({}); }
  };
  const markAns = (subj, qi, val, qc) => setMarkAnswers((a) => {
    const s = (a[subj] || "").padEnd(qc, " ").split("");
    s[qi] = s[qi] === val ? " " : val;
    return { ...a, [subj]: s.join("").trimEnd() };
  });
  const saveMark = async () => {
    if (!markStudent) return toast.error("Öğrenci seçin");
    try {
      await api.post("/kurum-results/bulk", { kurum_id: activeK.id, week_number: week, results: [{ student_id: markStudent, answers: markAnswers }] });
      const { data } = await api.get("/kurum-results", { params: { kurum_id: activeK.id, week_number: week, student_id: markStudent } });
      setMarkResult(data[0] || null);
      toast.success("Optik kaydedildi & değerlendirildi ✓");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delResult = async (sid) => {
    if (!window.confirm("Bu öğrencinin bu haftaki sonucu silinsin mi?")) return;
    try {
      await api.delete("/kurum-results", { params: { kurum_id: activeK.id, week_number: week, student_id: sid } });
      toast.success("Sonuç silindi ✓");
      openEval();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openBulkKarne = async () => {
    if (!evalRows.length) return;
    setBulkLoading(true);
    try {
      const items = await Promise.all(evalRows.map((r) => api.get(`/kurum-sinavlar/${activeK.id}/week/${week}/karne`, { params: { student_id: r.student_id } }).then(({ data }) => data)));
      setBulkKarne({ title: `${activeK.name} — ${week}. Hafta`, items });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBulkLoading(false);
  };

  const openEval = async () => { const { data } = await api.get(`/kurum-sinavlar/${activeK.id}/week/${week}/evaluation`); setEvalRows(data.rows); setView("eval"); };
  const openSeries = async () => { const { data } = await api.get(`/kurum-sinavlar/${activeK.id}/series`); setSeriesRows(data.rows); setView("series"); };

  const toggleRank = async () => {
    if (rankRows) { setRankRows(null); return; }
    try { const { data } = await api.get(`/kurum-sinavlar/${activeK.id}/week/${week}/evaluation`); setRankRows(data.rows); }
    catch { toast.error("Sıralama yüklenemedi"); }
  };

  const openKarne = async (sid) => {
    setKarneSid(sid);
    try {
      const { data } = await api.get(`/kurum-sinavlar/${activeK.id}/week/${week}/karne`, { params: sid ? { student_id: sid } : {} });
      setKarne(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const inp = "rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-400";
  const cls = activeK ? students.filter((s) => gradesOf(activeK).includes(s.grade)) : [];
  const shown = isEditor && gradeFilter ? list.filter((k) => gradesOf(k).includes(gradeFilter)) : list;

  // ---- Kurum listesi ----
  if (!activeK) {
    return (
      <div className="space-y-4">
        {isEditor && (
          <button data-testid="kurum-new-btn" onClick={openNew} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95"><Plus className="h-5 w-5" /> Yeni Kurum Sınavı</button>
        )}
        {isEditor && list.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-bold text-slate-400">Sınıf:</span>
            <button data-testid="kurum-filter-all" onClick={() => setGradeFilter(null)} className={`rounded-lg px-3 py-1 text-xs font-bold transition ${gradeFilter === null ? "bg-indigo-600 text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-indigo-300"}`}>Tümü</button>
            {GRADES.map((g) => (
              <button key={g} data-testid={`kurum-filter-${g}`} onClick={() => setGradeFilter(g)} className={`rounded-lg px-3 py-1 text-xs font-bold transition ${gradeFilter === g ? "bg-indigo-600 text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-indigo-300"}`}>{g}</button>
            ))}
          </div>
        )}
        {shown.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">{list.length === 0 ? <>Henüz kurum sınavı yok.{isEditor && ' "Yeni Kurum Sınavı" ile bir deneme serisi oluşturun.'}</> : <>{gradeFilter}. sınıfa atanmış kurum sınavı yok. <button data-testid="kurum-filter-clear" onClick={() => setGradeFilter(null)} className="font-bold text-indigo-600 underline">Tüm sınıfları göster</button></>}</div>
        ) : shown.map((k) => (
          <div key={k.id} data-testid={`kurum-card-${k.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <button data-testid={`kurum-open-${k.id}`} onClick={() => openKurum(k)} className="flex items-center gap-3 text-left">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600"><Building2 className="h-6 w-6" /></div>
              <div>
                <div className="font-bold text-slate-800">{k.name}</div>
                <div className="text-xs text-slate-500">{gradesOf(k).join(", ")}. Sınıf · {k.publisher || "—"} · {k.week_count} hafta · {(k.subjects || []).map((s) => `${s.name}(${s.question_count})`).join(", ")}</div>
              </div>
            </button>
            {isEditor && (
              <div className="flex items-center gap-1">
                <button data-testid={`kurum-edit-${k.id}`} onClick={() => openEdit(k)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-indigo-600"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`kurum-del-${k.id}`} onClick={() => delKurum(k.id)} className="rounded-lg p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        ))}
        {editK && <KurumEditor editK={editK} setEditK={setEditK} onSave={saveKurum} onClose={() => setEditK(null)} subjectsList={subjectsList} />}
      </div>
    );
  }

  // ---- ÖĞRENCİ / VELİ görünümü (salt-okur) ----
  if (!isEditor) {
    return (
      <div className="space-y-4">
        <button data-testid="kurum-back" onClick={() => setActiveK(null)} className="flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-indigo-600"><ChevronLeft className="h-4 w-4" /> Kurum Sınavları</button>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="font-bold text-slate-800">{activeK.name} <span className="text-xs font-normal text-slate-400">· {activeK.publisher}</span></p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Array.from({ length: activeK.week_count }).map((_, i) => (
              <button key={i} data-testid={`kurum-week-${i + 1}`} onClick={() => changeWeek(i + 1)} className={`h-8 w-11 rounded-lg text-xs font-bold transition ${week === i + 1 ? "bg-indigo-600 text-white shadow" : "border border-slate-200 bg-white text-slate-500 hover:border-indigo-300"}`}>{i + 1}</button>
            ))}
          </div>
        </div>
        {myResult ? (
          <div data-testid="kurum-my-result" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="mb-3 font-bold text-slate-800">{week}. Hafta Sonucun</p>
            <div className="mb-4 grid grid-cols-4 gap-2 text-center">
              {[["Doğru", myResult.total?.dogru, "text-emerald-600"], ["Yanlış", myResult.total?.yanlis, "text-rose-600"], ["Boş", myResult.total?.bos, "text-slate-500"], ["Net", myResult.total?.net, "text-indigo-600"]].map(([l, v, c]) => (
                <div key={l} className="rounded-xl bg-slate-50 p-3"><p className="text-xs font-bold text-slate-400">{l}</p><p className={`text-2xl font-extrabold ${c}`}>{v}</p></div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="kurum-my-karne-btn" onClick={() => openKarne(viewStudentId)} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95"><FileCheck2 className="h-5 w-5" /> Sınav Karnem</button>
              <button data-testid="kurum-my-rank-btn" onClick={toggleRank} className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 font-bold text-slate-600 transition hover:border-indigo-300 hover:text-indigo-600"><BarChart3 className="h-5 w-5" /> Sınıf Sıralaması</button>
            </div>
            {rankRows && (
              <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs font-bold text-slate-500"><tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Öğrenci</th><th className="px-3 py-2">D</th><th className="px-3 py-2">Y</th><th className="px-3 py-2">B</th><th className="px-3 py-2">Net</th><th className="px-3 py-2">Puan</th></tr></thead>
                  <tbody>
                    {rankRows.map((r, i) => (
                      <tr key={r.student_id} data-testid={`kurum-rank-row-${i}`} className={`border-t border-slate-100 ${r.student_id === myResult?.student_id ? "bg-indigo-50 font-bold" : ""}`}>
                        <td className="px-3 py-1.5 text-slate-400">{i + 1}</td><td className="px-3 py-1.5">{r.name}{r.section ? ` /${r.section}` : ""}</td><td className="text-center text-emerald-600">{r.total.dogru}</td><td className="text-center text-rose-500">{r.total.yanlis}</td><td className="text-center text-slate-400">{r.total.bos}</td><td className="text-center font-extrabold text-indigo-600">{r.total.net}</td><td className="text-center font-bold text-[#16324F]">{r.puan ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <PuanNote rows={rankRows} />
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">{week}. hafta için sonucun henüz girilmemiş.</div>
        )}
        {karne && <KurumKarneModal karne={karne} onClose={() => setKarne(null)} isEditor={isEditor} onChanged={() => openKarne(karneSid)} />}
      </div>
    );
  }

  // ---- EDİTÖR: seçili kurum ----
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button data-testid="kurum-back" onClick={() => setActiveK(null)} className="flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-indigo-600"><ChevronLeft className="h-4 w-4" /> Kurum Sınavları</button>
        <div className="flex flex-wrap gap-2">
          {[["key", "Cevap Anahtarı"], ["mark", "Optik İşaretle"], ["bulk", "Toplu İşaretle"]].map(([k, l]) => (
            <button key={k} data-testid={`kurum-tab-${k}`} onClick={() => setView(k)} className={`rounded-lg px-4 py-1.5 text-sm font-bold ${view === k ? "bg-indigo-600 text-white" : "border border-slate-200 text-slate-600"}`}>{l}</button>
          ))}
          <button data-testid="kurum-tab-eval" onClick={openEval} className={`rounded-lg px-4 py-1.5 text-sm font-bold ${view === "eval" ? "bg-indigo-600 text-white" : "border border-slate-200 text-slate-600"}`}>Hafta Sonucu</button>
          <button data-testid="kurum-tab-series" onClick={openSeries} className={`rounded-lg px-4 py-1.5 text-sm font-bold ${view === "series" ? "bg-indigo-600 text-white" : "border border-slate-200 text-slate-600"}`}>Seri Geneli</button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <p className="font-bold text-slate-800">{activeK.name} <span className="text-xs font-normal text-slate-400">· {gradesOf(activeK).join(", ")}. Sınıf · {activeK.publisher}</span></p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {Array.from({ length: activeK.week_count }).map((_, i) => (
            <button key={i} data-testid={`kurum-week-${i + 1}`} onClick={() => changeWeek(i + 1)} className={`h-8 w-11 rounded-lg text-xs font-bold transition ${week === i + 1 ? "bg-indigo-600 text-white shadow" : "border border-slate-200 bg-white text-slate-500 hover:border-indigo-300"}`}>{i + 1}</button>
          ))}
        </div>
      </div>

      {view === "key" && (
          <div className="space-y-3">
            {weekSubs.map((s, si) => {
              const filled = (s.kazanims || []).filter(Boolean).length;
              return (
                <div key={si} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="font-bold text-slate-700">{s.name}</span>
                    <span className="text-xs text-slate-400">({s.question_count} soru)</span>
                    <button data-testid={`kurum-kz-toggle-${si}`} onClick={() => setKzOpen((o) => ({ ...o, [si]: !o[si] }))} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:border-indigo-300 hover:text-indigo-600">
                      <ChevronDown className={`h-3.5 w-3.5 transition ${kzOpen[si] ? "rotate-180" : ""}`} /> Soru Kazanımları ({filled}/{s.question_count})
                    </button>
                    <input data-testid={`kurum-key-${si}`} value={s.answer_key || ""} onChange={(e) => setKey(si, e.target.value)} placeholder={`Cevap anahtarı (ör. ${"ABCD".repeat(2).slice(0, Math.min(s.question_count, 8))})`} className={`${inp} ml-auto w-72 font-mono uppercase tracking-widest`} />
                  </div>
                  {kzOpen[si] && (
                    <div className="mt-2 grid max-h-64 gap-1 overflow-y-auto rounded-xl bg-slate-50 p-3 sm:grid-cols-2">
                      <datalist id={`kurum-kz-list-${si}`}>
                        {[...new Set([...(kazanimMap[s.name] || []), ...(s.kazanims || [])].filter(Boolean))].map((kz) => <option key={kz} value={kz} />)}
                      </datalist>
                      {Array.from({ length: s.question_count }).map((_, qi) => (
                        <div key={qi} className="flex items-center gap-2 text-xs">
                          <span className="w-10 shrink-0 font-bold text-slate-400">{qi + 1}. <span className="text-indigo-500">{(s.answer_key || "")[qi] || "—"}</span></span>
                          <input data-testid={`kurum-kz-${si}-${qi}`} list={`kurum-kz-list-${si}`} value={(s.kazanims || [])[qi] || ""} onChange={(e) => setKz(si, qi, e.target.value)} placeholder="MEB listesinden seçin veya elle yazın…" className="flex-1 rounded border border-slate-200 bg-white px-1.5 py-1 text-xs outline-none focus:border-indigo-400" />
                        </div>
                      ))}
                      <p className="col-span-full text-[11px] text-slate-400">MEB havuzundan seçebilir veya serbest metin yazabilirsiniz — elle yazdıklarınız karnede aynen görünür.</p>
                    </div>
                  )}
                </div>
              );
            })}
            <button data-testid="kurum-save-week" onClick={saveWeek} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 font-bold text-white transition hover:bg-emerald-700 active:scale-95"><Save className="h-5 w-5" /> Cevap Anahtarını Kaydet</button>
          </div>

      )}

      {view === "bulk" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-bold text-slate-800">{week}. Hafta — Toplu Optik Giriş <span className="text-xs font-normal text-slate-400">({gradesOf(activeK).join(", ")}. sınıflar)</span></p>
              <button data-testid="kurum-save-bulk" onClick={saveBulk} className="flex items-center gap-2 rounded-xl bg-amber-600 px-5 py-2.5 font-bold text-white transition hover:bg-amber-700 active:scale-95"><Save className="h-5 w-5" /> Tümünü Kaydet & Değerlendir</button>
            </div>
            {cls.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">Bu sınıflarda öğrenci yok.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs font-bold text-slate-500">
                    <tr><th className="px-3 py-2 text-left">Öğrenci</th>{weekSubs.map((s) => <th key={s.name} className="px-3 py-2 text-left">{s.name}<span className="ml-1 font-normal text-slate-400">({s.question_count})</span></th>)}</tr>
                  </thead>
                  <tbody>
                    {cls.map((st) => (
                      <tr key={st.id} data-testid={`kurum-row-${st.id}`} className="border-t border-slate-100">
                        <td className="whitespace-nowrap px-3 py-2 font-semibold text-slate-700">{st.name} <span className="text-xs text-slate-400">{st.grade}{st.section ? `/${st.section}` : ""}</span></td>
                        {weekSubs.map((s) => (
                          <td key={s.name} className="px-3 py-2">
                            <input data-testid={`kurum-ans-${st.id}-${s.name}`} value={bulk[st.id]?.[s.name] || ""} onChange={(e) => setBulkAns(st.id, s.name, e.target.value, s.question_count)} placeholder="ABCD…" className="w-36 rounded-lg border border-slate-200 px-2 py-1.5 font-mono text-sm uppercase tracking-widest outline-none focus:border-amber-400" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
      )}

      {view === "mark" && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="mb-2 font-bold text-slate-800">{activeK.name} — {week}. Hafta Optik İşaretle</p>
            <select data-testid="kurum-mark-student" value={markStudent} onChange={(e) => selectMarkStudent(e.target.value)} className={`${inp} w-full max-w-md`}>
              <option value="">Öğrenci seçin…</option>
              {cls.map((st) => <option key={st.id} value={st.id}>{st.name} ({st.grade}. Sınıf{st.section ? `/${st.section}` : ""})</option>)}
            </select>
          </div>
          {markStudent && weekSubs.map((s) => (
            <div key={s.name} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h4 className="mb-3 font-bold text-slate-700">{s.name} <span className="text-xs font-normal text-slate-400">({s.question_count} soru · cevap anahtarıyla karşılaştırılır)</span></h4>
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: s.question_count }).map((_, qi) => {
                  const cur = (markAnswers[s.name] || "")[qi];
                  return (
                    <div key={qi} data-testid={`kurum-mark-q-${s.name}-${qi}`} className="flex flex-col items-center gap-0.5 rounded-lg border border-slate-100 p-1.5">
                      <span className="text-[10px] font-bold text-slate-400">{qi + 1}</span>
                      <div className="flex gap-0.5">
                        {OPTS.map((o) => (
                          <button key={o} data-testid={`kurum-mark-q-${s.name}-${qi}-${o}`} onClick={() => markAns(s.name, qi, o, s.question_count)} className={`h-6 w-6 rounded-full border text-[11px] font-bold transition ${cur === o ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300 text-slate-500 hover:border-indigo-400"}`}>{o}</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {markStudent && (
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="kurum-mark-save" onClick={saveMark} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white transition hover:bg-emerald-700 active:scale-95"><Save className="h-5 w-5" /> Kaydet & Değerlendir</button>
              {markResult && <button data-testid="kurum-mark-karne" onClick={() => openKarne(markStudent)} className="flex items-center gap-2 rounded-xl border border-indigo-200 px-5 py-3 font-bold text-indigo-600 hover:bg-indigo-50"><FileCheck2 className="h-5 w-5" /> Karneyi Gör</button>}
            </div>
          )}
          {markResult && <div data-testid="kurum-mark-result" className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700">Toplam → D:{markResult.total.dogru} · Y:{markResult.total.yanlis} · B:{markResult.total.bos} · Net {markResult.total.net}</div>}
        </div>
      )}

      {view === "eval" && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 bg-[#1E293B] px-5 py-3 text-white"><BarChart3 className="h-5 w-5" /><span className="font-bold">{week}. Hafta — Sınıf Sıralaması</span>{evalRows.length > 0 && <button data-testid="kurum-bulk-karne-btn" onClick={openBulkKarne} disabled={bulkLoading} className="ml-auto flex items-center gap-1.5 rounded-lg bg-amber-500 px-3.5 py-1.5 text-xs font-bold text-white transition hover:bg-amber-600 active:scale-95 disabled:opacity-60"><Printer className="h-4 w-4" /> {bulkLoading ? "Karneler Hazırlanıyor…" : `Toplu Karne Al (${evalRows.length})`}</button>}</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-bold text-slate-500"><tr><th className="px-4 py-2 text-left">#</th><th className="px-4 py-2 text-left">Öğrenci</th><th className="px-3 py-2">D</th><th className="px-3 py-2">Y</th><th className="px-3 py-2">B</th><th className="px-3 py-2">Net</th><th className="px-3 py-2">LGS Puanı</th><th className="px-3 py-2">İşlemler</th></tr></thead>
            <tbody>
              {evalRows.length === 0 ? <tr><td colSpan={8} className="py-6 text-center text-slate-400">Bu haftada sonuç yok.</td></tr> : evalRows.map((r, i) => (
                <tr key={r.student_id} data-testid={`kurum-eval-row-${i}`} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-bold text-slate-400">{i + 1}</td>
                  <td className="px-4 py-2 font-semibold text-slate-700">{r.name}{r.section ? ` /${r.section}` : ""}</td>
                  <td className="px-3 py-2 text-center text-emerald-600">{r.total.dogru}</td>
                  <td className="px-3 py-2 text-center text-rose-600">{r.total.yanlis}</td>
                  <td className="px-3 py-2 text-center text-slate-500">{r.total.bos}</td>
                  <td className="px-3 py-2 text-center font-extrabold text-indigo-600">{r.total.net}</td>
                  <td data-testid={`kurum-eval-puan-${i}`} className="px-3 py-2 text-center font-bold text-[#16324F]">{r.puan ?? "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <button data-testid={`kurum-eval-karne-${i}`} onClick={() => openKarne(r.student_id)} className="rounded-lg bg-indigo-600 px-3 py-1 text-xs font-bold text-white transition hover:bg-indigo-700 active:scale-95">Karne</button>
                      <button data-testid={`kurum-eval-edit-${i}`} title="Düzelt" onClick={() => { selectMarkStudent(r.student_id); setView("mark"); }} className="rounded-lg bg-amber-500 px-2.5 py-1 text-xs font-bold text-white transition hover:bg-amber-600 active:scale-95"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`kurum-eval-del-${i}`} title="Sil" onClick={() => delResult(r.student_id)} className="rounded-lg bg-rose-500 px-2.5 py-1 text-xs font-bold text-white transition hover:bg-rose-600 active:scale-95"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "series" && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 bg-[#1E293B] px-5 py-3 text-white"><FileCheck2 className="h-5 w-5" /><span className="font-bold">Seri Geneli — Ortalama Net Sıralaması</span></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-bold text-slate-500"><tr><th className="px-4 py-2 text-left">#</th><th className="px-4 py-2 text-left">Öğrenci</th><th className="px-3 py-2">Girilen Hafta</th><th className="px-3 py-2">Toplam Net</th><th className="px-3 py-2">Ortalama Net</th></tr></thead>
            <tbody>
              {seriesRows.length === 0 ? <tr><td colSpan={5} className="py-6 text-center text-slate-400">Henüz sonuç yok.</td></tr> : seriesRows.map((r, i) => (
                <tr key={r.student_id} data-testid={`kurum-series-row-${i}`} className="border-t border-slate-100"><td className="px-4 py-2 font-bold text-slate-400">{i + 1}</td><td className="px-4 py-2 font-semibold text-slate-700">{r.name}{r.section ? ` /${r.section}` : ""}</td><td className="px-3 py-2 text-center text-slate-500">{r.weeks}</td><td className="px-3 py-2 text-center text-slate-600">{r.total_net}</td><td className="px-3 py-2 text-center font-extrabold text-indigo-600">{r.avg_net}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {karne && <KurumKarneModal karne={karne} onClose={() => setKarne(null)} isEditor={isEditor} onChanged={() => openKarne(karneSid)} />}
      {bulkKarne && <KurumBulkKarneModal title={bulkKarne.title} items={bulkKarne.items} onClose={() => setBulkKarne(null)} />}
    </div>
  );
}

export function PuanNote({ rows }) {
  const label = rows?.find((r) => r.puan_label)?.puan_label;
  if (!label) return null;
  return <p data-testid="lgs-puan-note" className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[11px] font-semibold text-slate-500">LGS Puanı: {label}</p>;
}

export function LgsStatsEditor({ value, onChange, subjects }) {
  const [open, setOpen] = useState(false);
  const st = value || { subjects: {}, min_tasp: "", max_tasp: "", kaynak: "" };
  const patch = (p) => onChange({ ...st, ...p });
  const patchSubj = (name, f, v) => patch({ subjects: { ...(st.subjects || {}), [name]: { ...((st.subjects || {})[name] || {}), [f]: v === "" ? undefined : Number(v) } } });
  return (
    <div className="mt-4 rounded-xl border border-slate-200">
      <button type="button" data-testid="lgs-stats-toggle" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-3 py-2 text-sm font-bold text-slate-600">
        <span>LGS Puan İstatistikleri <span className="font-normal text-slate-400">(opsiyonel — resmî MEB verisi)</span></span>
        <ChevronDown className={`h-4 w-4 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-2 border-t border-slate-100 p-3">
          <p className="text-[11px] text-slate-400">Boş bırakılırsa istatistikler sınava giren öğrencilerden hesaplanır ve puan <b>"Deneme puanı — resmî LGS puanı değildir"</b> olarak etiketlenir. Resmî kılavuz verileri (ders ortalamaları, standart sapmalar, min/maks TASP) girilirse puan resmî istatistikle hesaplanır. İptal edilen soru/muafiyet durumu desteklenmez; o durumda puan hesaplanmaz, netler gösterilir.</p>
          {subjects.map((s) => (
            <div key={s.name} className="flex items-center gap-2 text-xs">
              <span className="w-28 shrink-0 truncate font-bold text-slate-600">{s.name}</span>
              <input type="number" step="0.01" min={0} max={s.question_count} data-testid={`lgs-ort-${s.name}`} placeholder="Ortalama" value={st.subjects?.[s.name]?.ort ?? ""} onChange={(e) => patchSubj(s.name, "ort", e.target.value)} className="w-24 rounded border border-slate-200 px-1.5 py-1 outline-none focus:border-indigo-400" />
              <input type="number" step="0.01" min={0.01} max={s.question_count} data-testid={`lgs-std-${s.name}`} placeholder="Std. Sapma" value={st.subjects?.[s.name]?.std ?? ""} onChange={(e) => patchSubj(s.name, "std", e.target.value)} className="w-24 rounded border border-slate-200 px-1.5 py-1 outline-none focus:border-indigo-400" />
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <input type="number" step="0.01" min={0} data-testid="lgs-min-tasp" placeholder="Min TASP" value={st.min_tasp ?? ""} onChange={(e) => patch({ min_tasp: e.target.value === "" ? undefined : Number(e.target.value) })} className="w-28 rounded border border-slate-200 px-1.5 py-1 outline-none focus:border-indigo-400" />
            <input type="number" step="0.01" min={0} data-testid="lgs-max-tasp" placeholder="Maks TASP" value={st.max_tasp ?? ""} onChange={(e) => patch({ max_tasp: e.target.value === "" ? undefined : Number(e.target.value) })} className="w-28 rounded border border-slate-200 px-1.5 py-1 outline-none focus:border-indigo-400" />
            <input data-testid="lgs-kaynak" placeholder="Veri kaynağı (ör. MEB 2026 LGS Kılavuzu)" value={st.kaynak ?? ""} onChange={(e) => patch({ kaynak: e.target.value })} className="min-w-48 flex-1 rounded border border-slate-200 px-1.5 py-1 outline-none focus:border-indigo-400" />
          </div>
        </div>
      )}
    </div>
  );
}

function KurumEditor({ editK, setEditK, onSave, onClose, subjectsList }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => { subjectsList(editK.grades?.length ? editK.grades : [editK.grade]).then(setOpts); }, [JSON.stringify(editK.grades)]);
  const inp = "rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-400";
  const patchSub = (i, patch) => setEditK((k) => ({ ...k, subjects: k.subjects.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const selGrades = editK.grades?.length ? editK.grades : [editK.grade];
  const toggleGrade = (g) => setEditK((k) => {
    const cur = k.grades?.length ? [...k.grades] : [k.grade];
    const next = cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g].sort((a, b) => a - b);
    if (!next.length) return k;
    return { ...k, grades: next, grade: next[0] };
  });
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-xl font-bold">{editK.id ? "Kurum Sınavını Düzenle" : "Yeni Kurum Sınavı"}</h3><button data-testid="kurum-ex-close" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="kurum-ex-name" value={editK.name} onChange={(e) => setEditK({ ...editK, name: e.target.value })} placeholder="Ad (ör. 4.SINIF JUMBO)" className={`${inp} col-span-2`} />
          <input data-testid="kurum-ex-publisher" value={editK.publisher} onChange={(e) => setEditK({ ...editK, publisher: e.target.value })} placeholder="Yayınevi" className={inp} />
          <div className="flex items-center gap-2"><label className="text-sm font-bold text-slate-500">Hafta</label><input data-testid="kurum-ex-weeks" type="number" min={1} max={52} value={editK.week_count} onChange={(e) => setEditK({ ...editK, week_count: Number(e.target.value) || 1 })} className={`${inp} w-20`} /></div>
          <div className="col-span-2">
            <p className="mb-1.5 text-sm font-bold text-slate-600">Sınıflara Ata <span className="font-normal text-slate-400">(birden fazla seçilebilir — sınav seçilen tüm sınıflara atanır)</span></p>
            <div className="flex flex-wrap gap-1.5">
              {GRADES.map((g) => {
                const on = selGrades.includes(g);
                return <button key={g} data-testid={`kurum-grade-chip-${g}`} onClick={() => toggleGrade(g)} className={`rounded-lg border px-3.5 py-1.5 text-sm font-bold transition ${on ? "border-indigo-600 bg-indigo-600 text-white shadow" : "border-slate-200 bg-white text-slate-500 hover:border-indigo-300"}`}>{g}. Sınıf</button>;
              })}
            </div>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <p className="text-sm font-bold text-slate-600">Dersler ve Soru Sayıları</p>
          {editK.subjects.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <input data-testid={`kurum-sub-name-${i}`} list="kurum-subj-list" value={s.name} onChange={(e) => patchSub(i, { name: e.target.value })} placeholder="Ders adı" className={`${inp} flex-1`} />
              <input data-testid={`kurum-sub-qc-${i}`} type="number" min={1} max={50} value={s.question_count} onChange={(e) => patchSub(i, { question_count: Number(e.target.value) || 1 })} className={`${inp} w-20`} />
              <button data-testid={`kurum-sub-del-${i}`} onClick={() => setEditK((k) => ({ ...k, subjects: k.subjects.filter((_, j) => j !== i) }))} className="rounded-lg p-1.5 text-rose-400 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <datalist id="kurum-subj-list">{opts.map((s) => <option key={s.id} value={s.name} />)}</datalist>
          <button data-testid="kurum-add-sub" onClick={() => setEditK((k) => ({ ...k, subjects: [...k.subjects, { name: "", question_count: 10 }] }))} className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 py-2 text-sm font-bold text-slate-500 hover:border-indigo-400 hover:text-indigo-600"><Plus className="h-4 w-4" /> Ders Ekle</button>
        </div>
        <LgsStatsEditor value={editK.lgs_stats} onChange={(v) => setEditK((k) => ({ ...k, lgs_stats: v }))} subjects={editK.subjects} />
        <button data-testid="kurum-save-exam" onClick={onSave} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95"><Save className="h-5 w-5" /> Kaydet</button>
      </div>
    </div>
  );
}

export function KurumKarneModal({ karne, onClose, isEditor = false, onChanged = null }) {
  const doPrint = () => {
    document.body.classList.add("print-karne");
    const done = () => { document.body.classList.remove("print-karne"); window.removeEventListener("afterprint", done); };
    window.addEventListener("afterprint", done);
    window.print();
  };
  const addExtra = async (subject, kazanim) => {
    try {
      await api.post("/kurum-results/extra-topic", { subject, kazanim }, { params: { kurum_id: karne.kurum?.id, week_number: karne.week_number, student_id: karne.student?.id } });
      toast.success("Kazanım eklendi ✓");
      if (onChanged) onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeExtra = async (subject, kazanim) => {
    try {
      await api.delete("/kurum-results/extra-topic", { params: { kurum_id: karne.kurum?.id, week_number: karne.week_number, student_id: karne.student?.id, subject, kazanim } });
      if (onChanged) onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const ICON = { dogru: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />, yanlis: <XCircle className="h-3.5 w-3.5 text-rose-500" />, bos: <MinusCircle className="h-3.5 w-3.5 text-slate-400" /> };
  return (
    <div className="karne-overlay fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={onClose}>
      <div data-testid="kurum-karne-modal" className="karne-print my-8 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="no-print mb-3 flex items-center justify-end gap-2">
          <button data-testid="kurum-karne-print" onClick={doPrint} className="flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 active:scale-95"><Printer className="h-4 w-4" /> Yazdır / PDF</button>
          <button data-testid="kurum-karne-close" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <KurumKarneBody karne={karne} onExtraAdd={isEditor ? addExtra : null} onExtraRemove={isEditor ? removeExtra : null} />
      </div>
    </div>
  );
}

export function KurumKarneBody({ karne, onExtraAdd = null, onExtraRemove = null }) {
  const { kurum, week_number, result, student, stats, analysis, history, puan } = karne;
  const subjects = Object.entries(result.per_subject || {}).map(([name, sc]) => ({
    name, soru: sc.soru, dogru: sc.dogru, yanlis: sc.yanlis, bos: sc.bos, net: sc.net,
    rank: analysis?.[name]?.rank, basari: sc.soru ? Math.round((sc.net / sc.soru) * 100) : 0,
    class_pct: analysis?.[name]?.class_pct, detay: sc.detay || [], topics: analysis?.[name]?.topics || [],
    extra: (result.extra_topics || []).filter((t) => t.subject === name).map((t) => t.kazanim),
  }));
  const d = {
    docTitle: "KURUM SINAVI SONUÇ BELGESİ",
    examName: kurum?.name, subInfo: `${week_number}. Hafta${kurum?.publisher ? ` · ${kurum.publisher}` : ""}`,
    student, total: result.total, maxNet: stats?.total_questions || 0,
    circles: [
      { label: `Netiniz (Maks:${stats?.total_questions ?? "—"})`, value: result.total.net, color: "#16324F" },
      { label: "Sınıf Ortalaması", value: stats?.avg_net ?? "—", color: "#64748B" },
      { label: "En Yüksek Net", value: stats?.max_net ?? "—", color: "#94A3B8" },
    ],
    ranks: [{ label: "Sınıf", count: stats?.count, rank: stats?.rank }],
    history: history || [],
    subjects,
    onExtraAdd, onExtraRemove,
    puan: puan || null,
  };
  return <SonucBelgesi d={d} />;
}

export function KurumBulkKarneModal({ title, items, onClose }) {
  const doPrint = () => {
    document.body.classList.add("print-karne");
    const done = () => { document.body.classList.remove("print-karne"); window.removeEventListener("afterprint", done); };
    window.addEventListener("afterprint", done);
    window.print();
  };
  return (
    <div className="karne-overlay fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={onClose}>
      <div data-testid="kurum-bulk-karne-modal" className="karne-print my-8 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="no-print mb-4 flex items-center justify-between gap-3">
          <h3 className="font-display text-xl font-extrabold text-slate-900">{title} — Toplu Karne ({items.length} öğrenci)</h3>
          <div className="flex items-center gap-2">
            <button data-testid="kurum-bulk-karne-print" onClick={doPrint} className="flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 active:scale-95"><Printer className="h-4 w-4" /> Yazdır / PDF</button>
            <button data-testid="kurum-bulk-karne-close" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
          </div>
        </div>
        {items.map((k, i) => (
          <div key={i} data-testid={`bulk-karne-${i}`} className="karne-block mb-6 rounded-2xl border-2 border-slate-200 p-4">
            <KurumKarneBody karne={k} />
          </div>
        ))}
      </div>
    </div>
  );
}
